/* eslint-disable max-len */
/* eslint-disable no-param-reassign */

import React, { useState, useCallback } from 'react';

import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { MLModalService } from 'ml-portals-services-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import '@xengage/gw-platform-translations';
import messages from './MLReferralAgentComponent.messages';
import metadata from './MLReferralAgentComponent.metadata.json5';
import styles from './MLReferralAgentComponent.module.scss';

function MLReferralAgentComponent(props) {
    const translator = useTranslator();

    const {
        quoteNumber,
        referralAgents,
        saveReferralAgentProducer,
        commissionOptions,
        setHasReferralAgentSelected,
        setIsReferralAgentValid
    } = props;

    const [isAddReferralAgents, setIsAddReferralAgents] = useState(referralAgents.length > 0);
    const maxReferralAgents = 5;
    const [oldValues, setOldValues] = useState([]);

    const {
        showProgressDialog
    } = MLModalService();

    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();

    const addReferralAgent = useCallback(() => {
        const newReferralProducer = {
            producerCode: '',
            producerName: '',
            eligibleVUE: false,
            errorMessage: ''
        };
        const oldValuesCopy = [...oldValues];
        oldValuesCopy.push('');
        setOldValues(oldValuesCopy);
        referralAgents.push(newReferralProducer);
        setIsReferralAgentValid(false);
    }, [oldValues, referralAgents, setIsReferralAgentValid]);

    const toggleAddReferralAgents = useCallback(() => {
        const toggle = !isAddReferralAgents;
        if (!toggle) {
            saveReferralAgentProducer([]);
            setOldValues([]);
        }
        setIsAddReferralAgents(toggle);
        setIsReferralAgentValid(false);
        commissionOptions.isReferralAgentSelected = toggle;
        setHasReferralAgentSelected(toggle);
    }, [commissionOptions, isAddReferralAgents, saveReferralAgentProducer, setHasReferralAgentSelected, setIsReferralAgentValid]);

    const _checkReferralAgentsEligibility = useCallback(() => {
        return !referralAgents.some((referralAgent) => {return referralAgent.eligibleVUE !== null && !referralAgent.eligibleVUE})
    }, [referralAgents]);

    const producerCodeChanged = useCallback((agent, index) => {
        if (agent.producerCode === '') {
            agent.eligibleVUE = false;
            const oldValuesCopy = [...oldValues];
            oldValuesCopy[index] = agent.producerCode;
            setOldValues(oldValuesCopy);
        } else if (agent.producerCode !== '' && agent.producerCode !== oldValues[index]) {
            const oldValuesCopy = [...oldValues];
            oldValuesCopy[index] = agent.producerCode;
            setOldValues(oldValuesCopy);

            if (agent.producerCode.length < 6 || agent.producerCode.length > 7) {
                agent.eligibleVUE = false;
                agent.errorMessage = translator(messages.ProducerCodeCharLimit);
                setIsReferralAgentValid(false);
            } else {
                let duplicatedCount = 0;
                referralAgents.forEach((referralAgent, innerIndex) => {
                    if (referralAgents.length > 1 && agent.producerCode === referralAgent.producerCode && index !== innerIndex) {
                        duplicatedCount += 1;
                    }
                });
                if (duplicatedCount < 1) {
                    const agentEligibilityPromise = LoadSaveService.getProducerEligibilityExt(quoteNumber, agent.producerCode, authHeader);
                    showProgressDialog(translator(messages.ProducerCodeEligibility), agentEligibilityPromise, true);
                    agentEligibilityPromise.then((agentEligibility)=> {
                        agent.producerName = agentEligibility.producerName;
                        agent.eligibleVUE = agentEligibility.eligibleVUE;
                        agent.errorMessage = agentEligibility.errorMessage;                      
                        refreshData();
                        saveReferralAgentProducer(referralAgents);
                        setIsReferralAgentValid(_checkReferralAgentsEligibility());
                    });
                } else {
                    agent.eligibleVUE = false;
                    agent.errorMessage = translator(messages.ProducerCodeAlreadyInList);
                    setIsReferralAgentValid(false);
                }
            }
        }

        setIsReferralAgentValid(_checkReferralAgentsEligibility());
        saveReferralAgentProducer(referralAgents);
    }, [_checkReferralAgentsEligibility, authHeader, oldValues, quoteNumber, referralAgents, refreshData, saveReferralAgentProducer, setIsReferralAgentValid, showProgressDialog, translator]);

    const generateOverrides = () => {
        if (!referralAgents.length > 0) {
            return {};
        }
        const referralAgentsOverrides = referralAgents.map((referralAgent, index) => {
            return {
                [`referralAgentProducerCode${index}`]: {
                    value: referralAgent.producerCode,
                    onValueChange: (value) => { 
                        referralAgent.producerCode = value;
                        if(referralAgent.eligibleVUE) {
                            setIsReferralAgentValid(true);
                        }
                        producerCodeChanged(referralAgent, index)
                    }
                },
                [`statusCheckCompleteGreenCheck${index}`]: {
                    visible: referralAgent.eligibleVUE
                },
                [`statusCheckProducerName${index}`]: {
                    content: referralAgent.eligibleVUE ? referralAgent.producerName : referralAgent.errorMessage
                },
            };
        });

        return Object.assign({}, ...referralAgentsOverrides);
    };

    const overrides = {
        isAddReferralAgentsCheckBox: {
            value: commissionOptions.isReferralAgentSelected,
            onValueChange: toggleAddReferralAgents,
            disabled: commissionOptions.isSplitCommissionSelected
        },
        referralAgentsSelectedContainer: {
            visible: isAddReferralAgents
        },
        referralAgentsSelectedButton: {
            disabled: referralAgents.length >= maxReferralAgents || commissionOptions.isSplitCommissionSelected
        },
        referralAgentsTableIterable: {
            data: referralAgents
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            addReferralAgent,
            producerCodeChanged
        },
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default MLReferralAgentComponent;